if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia("(min-width: 750px)");

        // If there are no thumbnails (e.g. single-image products like the gift card)
        if (!this.elements.thumbnails) {
          this.initializeSelectedColorMedia();

          // Make sure the gallery itself is visible
          if (this.classList.contains("invisible")) {
            this.classList.remove("invisible");
          }

          // NEW: also unhide the custom mobile swiper on small screens
          const mobileSwiperContainer = document.querySelector(
            "#custom-mobile-swiper-container"
          );
          if (
            mobileSwiperContainer &&
            mobileSwiperContainer.classList.contains("invisible") &&
            window.innerWidth <= 768
          ) {
            mobileSwiperContainer.classList.remove("invisible");
          }

          return;
        }

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              ?.addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });

        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        this.initializeSelectedColorMedia();
      }

      getColorHandle(value) {
        if (!value) return "";

        if (typeof handleize === "function") {
          return handleize(value);
        }

        return String(value)
          .trim()
          .toLowerCase()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-")
          .replace(/^-+|-+$/g, "");
      }

      getLabelForInput(input) {
        if (!input) return null;

        return (
          document.querySelector(`label[for="${input.id}"]`) ||
          (input.nextElementSibling?.tagName === "LABEL" ? input.nextElementSibling : null)
        );
      }

      getStableColorValueForInput(input) {
        const label = this.getLabelForInput(input);

        return (
          label?.getAttribute("data-swatch-image-value") ||
          label?.getAttribute("data-swatch-value") ||
          input?.value ||
          ""
        );
      }

      getSelectedColorHandle() {
        const queryColor = new URLSearchParams(window.location.search).get(
          "color-print-name"
        );

        const colorNames = (this.getAttribute("color-print-names") || "")
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean);

        const colorInputs = Array.from(
          document.querySelectorAll('label.color-swatch[for]')
        )
          .map((label) => document.getElementById(label.getAttribute("for")))
          .filter(Boolean);

        const checkedInput = colorInputs.find((input) => input.checked);
        const checkedStableValue = this.getStableColorValueForInput(checkedInput);
        if (checkedStableValue) return this.getColorHandle(checkedStableValue);

        if (queryColor) {
          const queryInput = colorInputs.find((input) => {
            const label = this.getLabelForInput(input);
            const candidates = [
              input.value,
              label?.getAttribute("option-value"),
              label?.getAttribute("data-swatch-value"),
              label?.getAttribute("data-swatch-image-value"),
            ];

            return candidates.some((candidate) => {
              return this.getColorHandle(candidate) === this.getColorHandle(queryColor);
            });
          });

          const queryStableValue = this.getStableColorValueForInput(queryInput);
          if (queryStableValue) return this.getColorHandle(queryStableValue);

          return this.getColorHandle(queryColor);
        }

        const matchingInput = colorInputs.find((input) =>
          colorNames.some(
            (colorName) =>
              this.getColorHandle(colorName) === this.getColorHandle(input.value)
          )
        );

        return this.getColorHandle(
          this.getStableColorValueForInput(matchingInput) || colorNames[0]
        );
      }

      initializeSelectedColorMedia() {
        const colorHandle = this.getSelectedColorHandle();
        if (!colorHandle) return;

        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll("[data-media-id]")
        );
        const matchingMedia = mediaItems.find((item) => {
          const text = item.querySelector("noscript")?.innerText?.toLowerCase() || "";
          return (
            text.includes(colorHandle) &&
            (colorHandle.includes("-x-") || !text.includes(`-x-${colorHandle}`))
          );
        });

        if (!matchingMedia) {
          const initiallyActiveMedia =
            mediaItems.find((item) => item.classList.contains("is-active")) ||
            mediaItems[0];

          if (initiallyActiveMedia) {
            this.setActiveMedia(initiallyActiveMedia.dataset.mediaId, false);
          }

          return;
        }

        this.setActiveMedia(matchingMedia.dataset.mediaId, false, colorHandle);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, colorPrintName) {
        const activeMedia = this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
        );

        this.elements.viewer.querySelectorAll("[data-media-id]").forEach((element) => {
            element.classList.remove("is-active");
        });
        activeMedia?.classList.add("is-active");


        // Desktop gallery: filter images by selected color (if we have one)
        const imageContainingElems = this.elements.viewer.querySelectorAll(
          'li:not(.product__media-item.product__media-item--variant)[data-media-id] noscript'
        );

        let visibleImageCount = 0;

        imageContainingElems.forEach((elem, index) => {
          const imageContainer = elem.closest("li");
          if (!imageContainer) return;

          imageContainer.setAttribute("custom-media-position", index);
          imageContainer.classList.remove("display-none");

          if (colorPrintName) {
            const colorKey = colorPrintName.toLowerCase();
            const text = elem.innerText.toLowerCase();

            let shouldHide = false;

            // Hide images that don't belong to this color
            if (!text.includes(colorKey)) {
              shouldHide = true;
            } else if (!colorKey.includes("-x-") && text.includes(`-x-${colorKey}`)) {
              // Special case for "-x-" assets
              shouldHide = true;
            }

            if (shouldHide) {
              imageContainer.classList.add("display-none");
            } else {
              visibleImageCount += 1;
            }
          } else {
            visibleImageCount += 1;
          }

          // Always keep videos visible
          if (imageContainer.getAttribute("media-type") === "video") {
            imageContainer.classList.remove("display-none");
            visibleImageCount += 1;
          }
        });

        // Safety fallback: never allow the PDP gallery to render fully blank
        if (colorPrintName && visibleImageCount === 0) {
          console.warn(
            "[SOP Gallery] No media matched selected color. Showing all gallery media as fallback.",
            colorPrintName
          );

          imageContainingElems.forEach((elem) => {
            const imageContainer = elem.closest("li");
            if (!imageContainer) return;
            imageContainer.classList.remove("display-none");
          });
        }


        const mobileSwiperContainer = document.querySelector("#custom-mobile-swiper-container");

        if (this.classList.contains("invisible")) {
            this.classList.remove("invisible");
        }
        if (mobileSwiperContainer && mobileSwiperContainer.classList.contains("invisible")) {
            mobileSwiperContainer.classList.remove("invisible");
        }

        if (prepend) {
            // Sync the custom mobile Swiper with the selected color
            if (window.innerWidth <= 768 && colorPrintName) {
            const swiperEl = document.querySelector("#product-media-modal-custom-swiper-mobile");

            if (swiperEl && swiperEl.swiper) {
                const targetSlug = colorPrintName.toLowerCase();
                const slides = Array.from(
                swiperEl.swiper.slides || swiperEl.querySelectorAll(".swiper-slide")
                );

                const toSlug = (str) => {
                if (typeof handleize === "function") {
                    return handleize(str);
                }
                return str
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "");
                };

                let matchIndex = -1;

                // 1) Try to match using the color part of the alt text: "Product - Color Name"
                for (let i = 0; i < slides.length; i++) {
                const img = slides[i].querySelector("img");
                if (!img) continue;

                const alt = img.getAttribute("alt") || "";
                let colorPart = alt;
                const dashIndex = alt.indexOf(" - ");
                if (dashIndex !== -1) {
                    colorPart = alt.slice(dashIndex + 3);
                }

                const altSlug = toSlug(colorPart);
                if (altSlug === targetSlug) {
                    matchIndex = i;
                    break;
                }
                }

                // 2) If that failed, try matching by image filename (src) as a fallback
                if (matchIndex === -1) {
                for (let i = 0; i < slides.length; i++) {
                    const img = slides[i].querySelector("img");
                    if (!img) continue;

                    const src = img.getAttribute("src") || "";
                    const filename = src.split("/").pop().split("?")[0]; // e.g. Elsa-Top-Cranberry-Red-1.jpg
                    const fileSlug = filename.toLowerCase();

                    if (fileSlug.includes(targetSlug)) {
                    matchIndex = i;
                    break;
                    }
                }
                }

                // 3) Very loose fallback: alt text containing the handle with spaces instead of hyphens
                if (matchIndex === -1) {
                const looseNeedle = targetSlug.replace(/-/g, " ");
                for (let i = 0; i < slides.length; i++) {
                    const img = slides[i].querySelector("img");
                    if (!img) continue;

                    const alt = (img.getAttribute("alt") || "").toLowerCase();
                    if (alt.includes(looseNeedle)) {
                    matchIndex = i;
                    break;
                    }
                }
                }

                if (matchIndex !== -1) {
                swiperEl.swiper.slideTo(matchIndex, 0);
                swiperEl.swiper.update();
                }
            }
            }

            if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
            }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
            if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
                left: activeMedia.offsetLeft,
            });
            }
            if (!this.elements.thumbnails || this.dataset.desktopLayout === "stacked") {
            // no-op for stacked galleries
            }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }


      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));

        thumbnail.querySelector("button").setAttribute("aria-current", true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image =
          activeItem.querySelector(".product__modal-opener--image img") ||
          activeItem.querySelector(".product__modal-opener--image img");
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem?.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}
